// One shared store for the year sprites (see galleryImages.ts).
//
// The meta (`/all/sprites`) is fetched once per page load. A year's sprite is
// only downloaded when something from that year asks for it, and only handed
// out once the image has actually loaded: a cold sprite can answer 503 while
// the backend builds it, and a broken background is worse than none. Until
// then callers get null and fall back to their plain look.

import { useEffect, useSyncExternalStore } from "react";
import {
  DayKey,
  SPRITES_URL,
  SpriteMeta,
  spriteCellStyle,
  spriteUrl,
} from "./galleryImages";

type YearState = "loading" | "ready" | "failed";

let meta: SpriteMeta | null = null;
let metaRequested = false;
const years = new Map<number, YearState>();
const listeners = new Set<() => void>();
// Bumped on every change so useSyncExternalStore sees a new snapshot.
let version = 0;

const emit = () => {
  version += 1;
  listeners.forEach((l) => l());
};

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

const getVersion = () => version;

const RETRY_MS = [10_000, 20_000, 40_000];

function loadMeta() {
  if (metaRequested) return;
  metaRequested = true;
  fetch(SPRITES_URL)
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      if (data && Array.isArray(data.years) && data.cell && data.cols) {
        meta = data as SpriteMeta;
        emit();
      }
    })
    .catch(() => {
      // No sprites (older backend, offline): everything keeps its plain look.
    });
}

function loadYear(year: number, attempt = 0) {
  if (!meta) return;
  const entry = meta.years.find((y) => y.year === year);
  if (!entry) return;
  if (attempt === 0) {
    if (years.has(year)) return;
    years.set(year, "loading");
  }
  const img = new Image();
  img.decoding = "async";
  img.onload = () => {
    years.set(year, "ready");
    emit();
  };
  img.onerror = () => {
    const wait = RETRY_MS[attempt];
    if (wait === undefined) {
      years.set(year, "failed");
      emit();
      return;
    }
    window.setTimeout(() => loadYear(year, attempt + 1), wait);
  };
  img.src = spriteUrl(entry.year, entry.v);
}

/** Sprite meta plus a reader for ready years. Re-renders when either changes. */
export function useSpriteMeta() {
  useSyncExternalStore(subscribe, getVersion, getVersion);
  useEffect(() => {
    loadMeta();
  }, []);
  return {
    meta,
    isReady: (year: number) => years.get(year) === "ready",
    request: (year: number) => loadYear(year),
  };
}

/**
 * Background style for one day's sprite cell, or null until that year's
 * sprite is in the browser. Asking is what triggers the download.
 */
export function useSpriteCell(key: DayKey | null): React.CSSProperties | null {
  const { meta: m, isReady } = useSpriteMeta();
  const year = key?.year;
  useEffect(() => {
    if (m && year !== undefined) loadYear(year);
  }, [m, year]);
  if (!m || !key || !isReady(key.year)) return null;
  return spriteCellStyle(m, key);
}

/** Test hook: forget everything between tests. */
export function __resetSpritesForTests() {
  meta = null;
  metaRequested = false;
  years.clear();
  emit();
}
